import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IconFlame, IconChevronRight, IconMoodSmile, IconCamera, IconBook, IconBell, IconShare, IconNote, IconWallet, IconRobot } from "@tabler/icons-react";

interface MenuSheetProps {
	open: boolean;
	onClose: () => void;
	hasCouple?: boolean;
}

interface MenuItem {
	icon: React.ReactNode;
	iconBg: string;
	iconColor: string;
	title: string;
	description: string;
	to: string;
}

const MENU_ITEMS: MenuItem[] = [
	{
		icon: <IconMoodSmile size={20} />,
		iconBg: "color-mix(in srgb, #f472b6 12%, var(--color-surface))",
		iconColor: "#f472b6",
		title: "Mood",
		description: "Ekspresikan perasaan harianmu",
		to: "/moods",
	},
	{
		icon: <IconCamera size={20} />,
		iconBg: "color-mix(in srgb, #60a5fa 12%, var(--color-surface))",
		iconColor: "#60a5fa",
		title: "Cerita",
		description: "Berbagi foto hari ini",
		to: "/stories",
	},
	{
		icon: <IconBook size={20} />,
		iconBg: "color-mix(in srgb, #a78bfa 12%, var(--color-surface))",
		iconColor: "#a78bfa",
		title: "Jurnal",
		description: "Tulis catatan harian",
		to: "/journals",
	},
	{
		icon: <IconWallet size={20} />,
		iconBg: "color-mix(in srgb, #10b981 12%, var(--color-surface))",
		iconColor: "#10b981",
		title: "Keuangan",
		description: "Catat pemasukan & pengeluaran",
		to: "/expenses/dashboard",
	},
	{
		icon: <IconRobot size={20} />,
		iconBg: "color-mix(in srgb, #a78bfa 12%, var(--color-surface))",
		iconColor: "#a78bfa",
		title: "Asisten",
		description: "Tanya AI, atur semuanya",
		to: "/assistant",
	},
	{
		icon: <IconBell size={20} />,
		iconBg: "color-mix(in srgb, #fbbf24 12%, var(--color-surface))",
		iconColor: "#fbbf24",
		title: "Pengingat",
		description: "Atur pengingat penting",
		to: "/reminders",
	},
	{
		icon: <IconFlame size={20} />,
		iconBg: "color-mix(in srgb, #f97316 12%, var(--color-surface))",
		iconColor: "#f97316",
		title: "Streak",
		description: "Lihat berapa hari berturut-turut kalian kuis",
		to: "/streak",
	},
	{
		icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>,
		iconBg: "color-mix(in srgb, #ec4899 12%, var(--color-surface))",
		iconColor: "#ec4899",
		title: "Premium",
		description: "Upgrade fitur",
		to: "/premium",
	},
	{
		icon: <IconShare size={20} />,
		iconBg: "color-mix(in srgb, #3b82f6 12%, var(--color-surface))",
		iconColor: "#3b82f6",
		title: "Referral",
		description: "Ajak teman, dapat komisi",
		to: "/referral",
	},
	{
		icon: <IconNote size={20} />,
		iconBg: "color-mix(in srgb, #14b8a6 12%, var(--color-surface))",
		iconColor: "#14b8a6",
		title: "Catatan",
		description: "Catatan pribadi & tentang pasangan",
		to: "/notes",
	},
];

export function MenuSheet({ open, onClose, hasCouple = false }: MenuSheetProps) {
	const navigate = useNavigate();
	const [visible, setVisible] = useState(false);
	const [animating, setAnimating] = useState(false);
	const [swipeY, setSwipeY] = useState(0);
	const sheetRef = useRef<HTMLDivElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const touchStartRef = useRef(0);
	const touchDeltaRef = useRef(0);
	const sheetTopRef = useRef(false);

	useEffect(() => {
		if (open) {
			previousFocusRef.current = document.activeElement as HTMLElement;
			// Push a dummy history entry so Android back button triggers popstate
			window.history.pushState("", "");
			const handlePopState = () => {
				setAnimating(false);
				setTimeout(onClose, 280);
			};
			window.addEventListener("popstate", handlePopState);
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setVisible(true);
			requestAnimationFrame(() => {
				requestAnimationFrame(() => {
					setAnimating(true);
					sheetRef.current?.focus();
				});
			});
			return () => window.removeEventListener("popstate", handlePopState);
		} else {
			setAnimating(false);
			const t = setTimeout(() => {
				setVisible(false);
				previousFocusRef.current?.focus();
			}, 300);
			return () => clearTimeout(t);
		}
	}, [open]);

	useEffect(() => {
		if (!open) return;
		function handleKeyDown(e: KeyboardEvent) {
			if (e.key === "Escape") {
				setAnimating(false);
				setTimeout(onClose, 280);
			}
		}
		document.addEventListener("keydown", handleKeyDown);
		return () => document.removeEventListener("keydown", handleKeyDown);
	}, [open, onClose]);

	if (!visible) return null;

	function handleItemClick(to: string) {
		setAnimating(false);
		setTimeout(() => {
			navigate(to);
			onClose();
		}, 280);
	}

	return (
		<div
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 9999,
				display: "flex",
				alignItems: "flex-end",
				justifyContent: "center",
				pointerEvents: visible ? "auto" : "none",
			}}
		>
			<div
				style={{
					position: "absolute",
					inset: 0,
					background: "rgba(0,0,0,0)",
					transition: "background 300ms ease",
					...(animating ? { background: "rgba(0,0,0,0.35)" } : {}),
				}}
				onClick={() => {
					setAnimating(false);
					setTimeout(onClose, 280);
				}}
			/>

		<div
			ref={sheetRef}
			role="dialog"
			aria-modal="true"
			aria-labelledby="menu-dialog"
			tabIndex={-1}
			onTouchStart={(e) => {
				const el = e.currentTarget;
				sheetTopRef.current = el.scrollTop <= 0;
				if (sheetTopRef.current) {
					touchStartRef.current = e.touches[0].clientY;
					touchDeltaRef.current = 0;
				}
			}}
			onTouchMove={(e) => {
				if (!sheetTopRef.current) return;
				const delta = e.touches[0].clientY - touchStartRef.current;
				if (delta > 0) {
					touchDeltaRef.current = delta;
					setSwipeY(delta * 0.5);
				}
			}}
			onTouchEnd={() => {
				if (touchDeltaRef.current > 100) {
					setAnimating(false);
					setTimeout(onClose, 280);
				}
				setSwipeY(0);
				touchDeltaRef.current = 0;
				sheetTopRef.current = false;
			}}
			style={{
					position: "relative",
					width: "100%",
					maxWidth: 480,
					background: "var(--color-surface)",
					borderRadius: "var(--radius-xl) var(--radius-xl) 0 0",
					boxShadow: "0 -4px 24px rgba(0,0,0,0.12)",
					transform: animating
						? `translateY(${swipeY}px)`
						: "translateY(100%)",
					transition: swipeY
						? "none"
						: "transform 300ms cubic-bezier(0.32, 0.72, 0, 1)",
					paddingBottom: "env(safe-area-inset-bottom, 0px)",
					maxHeight: "70dvh",
					overflowY: "auto",
				}}
			>
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						padding: "0.75rem 0 0.25rem",
					}}
				>
					<div
						style={{
							width: 36,
							height: 4,
							borderRadius: 999,
							background: "var(--color-border)",
						}}
					/>
				</div>

				<div style={{ padding: "0.5rem 1.25rem 1rem" }}>
				<h3
					id="menu-dialog"
					style={{
						margin: "0 0 0.75rem",
						fontSize: "0.95rem",
						fontWeight: 700,
						color: "var(--color-text)",
					}}
				>
					Menu
				</h3>

					<div style={{ display: "grid", gap: "0.4rem" }}>
						{MENU_ITEMS.filter((item) => {
							// Hide couple-only items when no couple
							const coupleOnly = ["/streak", "/quiz", "/insights", "/stories"];
							if (!hasCouple && coupleOnly.includes(item.to)) return false;
							return true;
						}).map((item) => (
							<button
								key={item.to}
								type="button"
								onClick={() => handleItemClick(item.to)}
								style={{
									display: "flex",
									alignItems: "center",
									gap: "0.85rem",
									padding: "0.75rem",
									borderRadius: "var(--radius-md)",
									border: "none",
									background: "transparent",
									cursor: "pointer",
									textAlign: "left",
									width: "100%",
									transition: "background 150ms ease",
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.background =
										"var(--color-surface-2)")
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.background = "transparent")
								}
							>
								<div
									style={{
										width: 40,
										height: 40,
										borderRadius: "var(--radius-md)",
										background: item.iconBg,
										color: item.iconColor,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
									}}
								>
									{item.icon}
								</div>
								<div style={{ flex: 1, minWidth: 0 }}>
									<div
										style={{
											fontSize: "0.88rem",
											fontWeight: 600,
											color: "var(--color-text)",
											lineHeight: 1.3,
										}}
									>
										{item.title}
									</div>
									<div
										style={{
											fontSize: "0.78rem",
											color: "var(--color-muted)",
											lineHeight: 1.3,
											marginTop: 2,
										}}
									>
										{item.description}
									</div>
								</div>
								<IconChevronRight
									size={16}
									style={{
										color: "var(--color-muted)",
										flexShrink: 0,
									}}
								/>
							</button>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
